import { useMemo, type ReactNode } from 'react';
import { HotelRooms } from '@voll-intelligence/intelligence-ui-kit';

import type { GenUiDispatch } from '../../../shared/ui/action.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { rankGroups, type HotelRoomsBlock } from '@voll-intelligence/genui-contract';
import { createHotelRoomsView, resolveHotelRoomSelection } from '../../shared/hotel_rooms_view.js';
import { BLOCK_ELEVATION } from '../../../shared/ui/block_elevation.js';

/** GenUI lifecycle/action adapter around the shared controlled room presentation. */
export function HotelRoomsCard({
  ui,
  note,
  readOnly = false,
  busy = false,
  dispatch,
}: {
  readonly ui: HotelRoomsBlock;
  readonly note?: string;
  readonly readOnly?: boolean;
  readonly busy?: boolean;
  readonly dispatch: GenUiDispatch;
}): ReactNode {
  const { locale, proxyImageUrl } = useGenUiConfig();
  const ranked = useMemo(() => ({ ...ui, rooms: rankGroups(ui.rooms) }), [ui]);
  const view = useMemo(
    () => createHotelRoomsView(ranked, { locale, proxyImageUrl }),
    [ranked, locale, proxyImageUrl],
  );
  const displayProps =
    view.display === 'receipt'
      ? { display: view.display, receipt: view.receipt }
      : { display: view.display };

  return (
    /* `gap-6` (24px) é a distância entre a fala do agente e o bloco no
       protótipo (`grid-cols-[minmax(0,1fr)] gap-6` do turno). */
    <div className={`genui2 grid gap-6 ${BLOCK_ELEVATION}`}>
      {/* A fala do agente ABRE o turno, como no protótipo (o turno é `texto`
          e depois o bloco) e como todo outro card daqui (`itin-intro`). Estava
          embaixo, e a frase que apresenta a lista chegava depois dela. */}
      {note && <p className="whitespace-pre-wrap text-muted-foreground">{note}</p>}
      <HotelRooms
        rooms={view.rooms}
        copy={view.copy}
        context={view.context}
        {...(view.otherRooms ? { otherRooms: view.otherRooms } : {})}
        {...(view.selectedHotel ? { selectedHotel: view.selectedHotel } : {})}
        {...displayProps}
        /* Fase 1.7: `selectedRateRoomIndex` SEM `viewOnly` mantém `display`
           em `selection` — o agente só está destacando a tarifa citada, a
           pessoa não confirmou nada. `selectionConfirmed` segue `receipt` de
           propósito: recibo trava o CTA ("Selecionado"), destaque interativo
           revela a tarifa sem travar. */
        {...(view.selectedRateId
          ? { selectedRateId: view.selectedRateId, selectionConfirmed: view.display === 'receipt' }
          : {})}
        {...(view.initialFilters ? { initialFilters: view.initialFilters } : {})}
        readOnly={readOnly || view.readOnly}
        busy={busy}
        onRateSelect={(roomId, rateId) => {
          const action = resolveHotelRoomSelection(ranked, roomId, rateId, locale);
          if (action) dispatch(action);
        }}
        onRoomDetailsOpen={() => {}}
      />
    </div>
  );
}
